import React, { ChangeEvent, useEffect, useState } from 'react';
import { ResizableInput } from 'shared/components/ui/resizable-input';
import { TimeFormatOptions } from 'shared/types/date-format-options';
import { parseDateTime } from 'shared/utils/parse-date-time';
import { parseTimeToMsOfDay } from 'shared/utils/parse-time-to-ms-of-day';
import { processStringToHoursAndMinutes } from 'shared/utils/process-string-to-hours-and-minutes';

export type TimePickerInputProps = Omit<
  React.ComponentProps<typeof ResizableInput>,
  'onChange' | 'value'
> & {
  onChange: (value?: number) => void;
  timeFormat: TimeFormatOptions;
  value?: number | null;
  icon?: React.FC;
};

export const TimePickerInput: React.FC<TimePickerInputProps> = ({
  onChange,
  value,
  timeFormat,
  ...rest
}) => {
  const [inputValue, setInputValue] = useState<string>('');

  const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    if (!target.value.length) {
      // if empty, the user removed the time, so it doesn't want a Date
      onChange();
      setInputValue('');
      return;
    }

    // Process textual input to the format we expect
    const hoursAndMinutes = processStringToHoursAndMinutes(
      target.value,
      TimeFormatOptions.Military,
    );

    // The value was normalised to 24h above, so it must be read back as 24h too —
    // parsing it with the user's format yields an invalid date for 12h users.
    const msOfDay = parseTimeToMsOfDay(
      hoursAndMinutes,
      TimeFormatOptions.Military,
    );

    if (msOfDay !== null) {
      onChange(msOfDay);
      return;
    }

    // Update the input if it isn't a valid time format yet
    setInputValue(hoursAndMinutes);
  };

  useEffect(() => {
    let valString = '';

    if (value) {
      valString = parseDateTime(value, timeFormat);
    }

    setInputValue(valString);
  }, [value, timeFormat]);

  return (
    <ResizableInput value={inputValue} onChange={handleInputChange} {...rest} />
  );
};
